import { useEffect, useState } from "react";
import { trackEvent } from "./tracking";

type Props = {
  headline?: string;
  body?: string;
  ctaLabel?: string;
  ctaHref?: string;
  storageKey?: string;
  accent?: string;
};

export default function ExitIntentModal({
  headline = "Before you go — grab a free AI visibility Assessment",
  body = "We scan your site and show exactly where you're losing leads to competitors. Takes 60 seconds. No obligation.",
  ctaLabel = "Get my free Assessment",
  ctaHref = "/audit",
  storageKey = "exit_intent_seen_v1",
  accent = "#f0a500",
}: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (window.innerWidth < 768) return;
    if (sessionStorage.getItem(storageKey)) return;

    let armed = false;
    const arm = setTimeout(() => {
      armed = true;
    }, 8000);

    const handler = (e: MouseEvent) => {
      if (!armed) return;
      if (e.clientY <= 0 && e.relatedTarget === null) {
        setOpen(true);
        sessionStorage.setItem(storageKey, "1");
        trackEvent("exit_intent_shown", {});
        document.removeEventListener("mouseout", handler);
      }
    };
    document.addEventListener("mouseout", handler);

    return () => {
      clearTimeout(arm);
      document.removeEventListener("mouseout", handler);
    };
  }, [storageKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    trackEvent("exit_intent_dismiss", {});
  };
  const click = () => {
    trackEvent("exit_intent_convert", { href: ctaHref });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={close}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center"
        >
          ×
        </button>
        <div className="flex items-center gap-2 mb-3">
          <span
            className="inline-block w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${accent}22`, color: accent }}
            aria-hidden="true"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: accent }}>
            Wait
          </span>
        </div>
        <h2 id="exit-intent-title" className="text-2xl font-bold text-gray-900 mb-3">
          {headline}
        </h2>
        <p className="text-gray-600 mb-6 leading-relaxed">{body}</p>
        <a
          href={ctaHref}
          onClick={click}
          className="block w-full text-center py-3 px-6 rounded-lg font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: accent }}
        >
          {ctaLabel}
        </a>
        <button
          type="button"
          onClick={close}
          className="mt-3 w-full text-center text-sm text-gray-500 hover:text-gray-700"
        >
          No thanks, I'll pass
        </button>
      </div>
    </div>
  );
}
